// src/chat/ChatSetup.tsx

import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { DEFAULT_BELLA_SYSTEM_PROMPT } from "./defaultBellaPrompt";
import { getActiveLessonId, setActiveLesson, listLessonOptions } from "./lessonStore";
import type { LessonId } from "./lessonStore";

import {
  ensureSeeded,
  listVersions,
  getActiveVersion,
  setActiveVersion,
  createVersion,
  updateVersion,
  deleteVersion,
  exportOneVersion,
  exportAllVersions,
  importJson,
  PromptVersion,
  DEFAULT_WELCOME_MESSAGE,
} from "./promptStore";

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: "application/json;charset=utf-8" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();

  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export default function ChatSetup() {
  const nav = useNavigate();

  const [versions, setVersions] = React.useState<PromptVersion[]>([]);
  const [selectedId, setSelectedId] = React.useState<string>("");
  const [name, setName] = React.useState("");
  const [systemPrompt, setSystemPrompt] = React.useState("");
  const [welcomeMessage, setWelcomeMessage] = React.useState("");
  const [status, setStatus] = React.useState<string>("");
  const lessonOptions = React.useMemo(() => listLessonOptions(), []);

const [lessonId, setLessonId] = React.useState<LessonId | "">(getActiveLessonId() || "");

React.useEffect(() => {
  setLessonId(getActiveLessonId() || "");
}, []);

  const refresh = React.useCallback(() => {
    
    ensureSeeded(DEFAULT_BELLA_SYSTEM_PROMPT);
    const vs = listVersions();
    const active = getActiveVersion();

    setVersions(vs);

    const pickId = selectedId || active?.id || vs[0]?.id || "";
    setSelectedId(pickId);

    const picked = vs.find((v) => v.id === pickId) || active || vs[0];
    setName(picked?.name || "");
    setSystemPrompt(picked?.systemPrompt || "");
    setWelcomeMessage(picked?.welcomeMessage || DEFAULT_WELCOME_MESSAGE);
  }, [selectedId]);

  React.useEffect(() => {
    refresh();
  }, []);

  const picked = React.useMemo(
    () => versions.find((v) => v.id === selectedId) || null,
    [versions, selectedId]
  );

  React.useEffect(() => {
    if (!picked) return;
    setName(picked.name);
    setSystemPrompt(picked.systemPrompt);
    setWelcomeMessage(picked.welcomeMessage || DEFAULT_WELCOME_MESSAGE);
  }, [picked?.id]);

  const active = React.useMemo(() => getActiveVersion(), [versions]);

  const onSaveEdits = () => {
    if (!picked) return;

    updateVersion(picked.id, {
      name: name.trim() || picked.name,
      systemPrompt,
      welcomeMessage,
    });

    setStatus("Opgeslagen ✅");
    refresh();
  };

  const onSaveAsNew = () => {
    const n = (name || "Nieuwe versie").trim();
    const v = createVersion(n, systemPrompt, welcomeMessage);
    setStatus(`Nieuwe versie gemaakt: ${v.name} ✅`);
    setSelectedId(v.id);
    refresh();
  };

  const onSetActive = () => {
    if (!picked) return;
    setActiveVersion(picked.id);
    setStatus(`Actief gezet: ${picked.name} ✅`);
    refresh();
  };

  const onDelete = () => {
    if (!picked) return;
    const ok = window.confirm(`Verwijderen: "${picked.name}"?`);
    if (!ok) return;

    deleteVersion(picked.id);
    setStatus("Verwijderd 🗑️");
    setSelectedId("");
    refresh();
  };

  const onExportOne = () => {
    if (!picked) return;
    const json = exportOneVersion(picked.id);
    if (!json) return;

    const safe = picked.name.replace(/[^\w\-]+/g, "_").slice(0, 40) || "prompt";
    downloadText(`prompt_${safe}.json`, json);
    setStatus("Versie gedownload ✅");
  };

  const onExportAll = () => {
    const json = exportAllVersions();
    downloadText(`prompt_backup_all_versions.json`, json);
    setStatus("Backup gedownload ✅");
  };

  const onImport = async (file: File) => {
    const raw = await file.text();
    const result = importJson(raw);
    setStatus(`Import klaar ✅ (nieuw: ${result.added}, bijgewerkt: ${result.updated})`);
    setSelectedId("");
    refresh();
  };

  return (
    <div style={{ maxWidth: 1080, margin: "0 auto", padding: "28px 16px 60px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 26, margin: 0 }}>Chat Setup</h1>
          <p style={{ marginTop: 8, opacity: 0.75 }}>
            Beheer je Bella versies. Zet één versie actief en test daarna in de chat.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>

          
          
         <button
  type="button"
  onClick={() => {
  if (!picked || !selectedId) {
    nav("/chat/play");
    return;
  }

  const nextName = (name.trim() || picked.name).trim();

  const isDirty =
    nextName !== (picked.name || "").trim() ||
    (systemPrompt || "") !== (picked.systemPrompt || "") ||
    (welcomeMessage || "") !== (picked.welcomeMessage || DEFAULT_WELCOME_MESSAGE);

  if (isDirty) {
    // save current edits into the selected version
    updateVersion(picked.id, {
      name: nextName,
      systemPrompt,
      welcomeMessage,
    });

    setStatus("Opgeslagen ✅ Chat openen…");
  }

  // make sure chat uses this version
  setActiveVersion(selectedId);

  // tiny delay so the status flashes and React state settles
  setTimeout(() => nav("/chat/play"), 120);
}}

  style={{
    borderRadius: 999,
    padding: "10px 14px",
    border: "1px solid rgba(20,20,40,0.12)",
    background: "rgba(123, 97, 255, 0.95)",
    color: "white",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 10px 24px rgba(123,97,255,0.22)",
  }}
>
  Open chat →
</button>




        </div>
      </div>

      {status ? (
        <div
          style={{
            marginTop: 14,
            padding: "10px 12px",
            borderRadius: 12,
            background: "rgba(34,197,94,0.10)",
            border: "1px solid rgba(34,197,94,0.25)",
            color: "rgba(10,60,25,0.9)",
            fontWeight: 600,
          }}
        >
          {status}
        </div>
      ) : null}

      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16, marginTop: 18 }}>
        {/* Left: versions */}
        <div
          style={{
            borderRadius: 16,
            padding: 14,
            border: "1px solid rgba(20,20,40,0.12)",
            background: "rgba(255,255,255,0.65)",
            boxShadow: "0 10px 30px rgba(20,20,40,0.08)",
            height: "fit-content",
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 10 }}>Versies</div>

          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            style={{
              width: "100%",
              borderRadius: 12,
              padding: "10px 10px",
              border: "1px solid rgba(20,20,40,0.14)",
              background: "rgba(255,255,255,0.85)",
              fontWeight: 600,
            }}
          >
            {versions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
                {active?.id === v.id ? "  (ACTIEF)" : ""}
              </option>
            ))}
          </select>

          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={onSetActive}
              disabled={!picked}
              style={{
                borderRadius: 999,
                padding: "9px 12px",
                border: "1px solid rgba(20,20,40,0.12)",
                background: "rgba(255,255,255,0.85)",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Zet actief
            </button>

            <button
              type="button"
              onClick={onExportOne}
              disabled={!picked}
              style={{
                borderRadius: 999,
                padding: "9px 12px",
                border: "1px solid rgba(20,20,40,0.12)",
                background: "rgba(255,255,255,0.85)",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Export 1
            </button>

            <button
              type="button"
              onClick={onExportAll}
              style={{
                borderRadius: 999,
                padding: "9px 12px",
                border: "1px solid rgba(20,20,40,0.12)",
                background: "rgba(255,255,255,0.85)",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Export alles
            </button>

            <label
              style={{
                borderRadius: 999,
                padding: "9px 12px",
                border: "1px solid rgba(20,20,40,0.12)",
                background: "rgba(255,255,255,0.85)",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Import
              <input
                type="file"
                accept="application/json"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  onImport(f);
                  e.currentTarget.value = "";
                }}
              />
            </label>

            <button
              type="button"
              onClick={onDelete}
              disabled={!picked || versions.length <= 1}
              title={versions.length <= 1 ? "Je kunt niet je laatste versie verwijderen" : "Verwijderen"}
              style={{
                borderRadius: 999,
                padding: "9px 12px",
                border: "1px solid rgba(239,68,68,0.25)",
                background: "rgba(239,68,68,0.08)",
                fontWeight: 800,
                cursor: versions.length <= 1 ? "not-allowed" : "pointer",
                opacity: versions.length <= 1 ? 0.5 : 1,
              }}
            >
              Verwijder
            </button>
          </div>

          <div style={{ marginTop: 12, fontSize: 12, opacity: 0.7, lineHeight: 1.35 }}>
            Tip: zet een versie actief → open chat → test → kom terug om te tweaken.
          </div>
        </div>

        {/* Right: editor */}
        <div
          style={{
            borderRadius: 16,
            padding: 14,
            border: "1px solid rgba(20,20,40,0.12)",
            background: "rgba(255,255,255,0.65)",
            boxShadow: "0 10px 30px rgba(20,20,40,0.08)",
          }}
        >
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ fontWeight: 800 }}>Naam</div>
            
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{
                flex: 1,
                minWidth: 240,
                borderRadius: 12,
                padding: "10px 10px",
                border: "1px solid rgba(20,20,40,0.14)",
                background: "rgba(255,255,255,0.85)",
                fontWeight: 700,
              }}
            />
          </div>

          <div
  style={{
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    marginTop: "10px",
    borderRadius: 14,
    border: "1px solid rgba(20,20,40,0.12)",
    background: "rgba(255,255,255,0.65)",
    boxShadow: "0 10px 30px rgba(20,20,40,0.06)",
  }}
>
  <div style={{ fontWeight: 900, fontSize: 12, opacity: 0.8, whiteSpace: "nowrap" }}>
    Les pakket
  </div>

  <select
    value={lessonId}
    onChange={(e) => {
      const v = e.target.value as LessonId | "";
      setLessonId(v);
      setActiveLesson(v ? v : null);
      setStatus(v ? "Les pakket geselecteerd ✅" : "Les pakket uitgezet ✅");
    }}
    style={{
      minWidth: 260,
      borderRadius: 12,
      padding: "10px 10px",
      border: "1px solid rgba(20,20,40,0.14)",
      background: "rgba(255,255,255,0.85)",
      fontWeight: 800,
    }}
  >
    <option value="">Geen (alleen chat)</option>
    {lessonOptions.map((o) => (
      <option key={o.id} value={o.id}>
        {o.label}
      </option>
    ))}
  </select>
</div>

          <div style={{ marginTop: 18, fontWeight: 800 }}>Welcome message</div>
          <textarea
            value={welcomeMessage}
            onChange={(e) => setWelcomeMessage(e.target.value)}
            style={{
              width: "100%",
              marginTop: 8,
              minHeight: 90,
              resize: "vertical",
              borderRadius: 14,
              padding: 12,
              border: "1px solid rgba(20,20,40,0.14)",
              background: "rgba(255,255,255,0.88)",
              fontSize: 14,
              lineHeight: 1.5,
            }}
          />

          <div style={{ marginTop: 18, fontWeight: 800 }}>System prompt</div>
          <textarea
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
            spellCheck={false}
            style={{
              width: "100%",
              marginTop: 8,
              minHeight: 420,
              resize: "vertical",
              borderRadius: 14,
              padding: 12,
              border: "1px solid rgba(20,20,40,0.14)",
              background: "rgba(255,255,255,0.88)",
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              fontSize: 12.5,
              lineHeight: 1.5,
            }}
          />

          <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={onSaveEdits}
              disabled={!picked}
              style={{
                borderRadius: 999,
                padding: "10px 14px",
                border: "1px solid rgba(20,20,40,0.12)",
                background: "rgba(255,255,255,0.85)",
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              Opslaan (edit)
            </button>

            <button
              type="button"
              onClick={onSaveAsNew}
              style={{
                borderRadius: 999,
                padding: "10px 14px",
                border: "1px solid rgba(20,20,40,0.12)",
                background: "rgba(123, 97, 255, 0.95)",
                color: "white",
                fontWeight: 900,
                cursor: "pointer",
                boxShadow: "0 10px 24px rgba(123,97,255,0.22)",
              }}
            >
              Save as new version
            </button>

           

          
          </div>

          <div style={{ marginTop: 10, fontSize: 12, opacity: 0.7 }}>
            Let op: wijzigingen zijn zichtbaar zodra je een nieuw gesprek start (of in de chat op Restart klikt).
          </div>
        </div>
      </div>
    </div>
  );
}
